import {
  Col, Row,
} from 'antd';
import BuyerGroupCard from 'Components/common/components/BuyerGroupCard';
import GlobalButton from 'Components/common/components/GlobalButton';
import HeadingWithToolTip from 'Components/common/components/HeadingWithToolTip';
import NoPersonsFound from 'Components/common/components/NoPersonsFound';

import BuyerGroupSkeleton from 'Components/common/skeleton/BuyerGroupSkeleton';
import SkeletonCustom from 'Components/common/skeleton/SkeletonCustom';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { StaffBuyerGroupData } from 'Types/GroupsTypes';
import {
  NO_GROUP_FOUND, translateSkipAreas, TOOLTIP_BUYERGROUP, VIDEO_BUYERGROUP,
} from 'Utils/Constants';
import {
  formatNumberWithCommas,
  IsMobile, valueToObject,
  isEnterpriseUser
} from 'Utils/UtilityFunctions';
import {
  boxShadow, secondaryFont, wMedium, primaryHeadingColor,
  maxWidth, retina, wMBold, maxTablet, maxMobile, scrollbarTransparent, minWidthApp,
  midMobile,
} from 'Utils/utilsStyle';
import { useDispatch, useSelector } from 'react-redux';
import { actions as certificationActions } from 'Slice/CertificationSlice';
import { selectData } from 'Selector/CertificationSelector';
import { actions as flurryActions } from 'Slice/HelperSlice';
import DrawerPeopleGroup from './DrawerPeopleGroup';
import DrawerPersonas from 'Components/people/components/DrawerPersonas';

type Props = {
  loading: boolean
  title: string
  groupCount: number
  groups: StaffBuyerGroupData[]
  dragging: boolean
  loaded: boolean
};

const Wrapper = styled.div`
  background-color: rgba(255,255,255, 0.2);
  box-shadow: ${boxShadow};
  border-radius: 16px;
  padding: 36px;
  margin-top: 40px;
  @media all and (max-width: ${maxWidth}) , ${retina} { 
    padding: 15px 25px 25px 25px;
  }
  .head-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    .right-area {
      display: flex;
      align-items: center;
      .create-new-button {
        @media all and (max-width: ${maxTablet}) {
          font-size: 11px;
          padding: 2px 6px;
        }
      }
    }
    &.smMob-wrapper {
      @media all and (max-width: ${midMobile}){
        flex-direction: column;
        align-items: unset;
        .most-active-group {
          justify-content: flex-end;
          margin-top: 8px;
        }
      }
    }
  }
  .tablet-row {
    @media all and (max-width: ${maxTablet}) {
      ${scrollbarTransparent};
      flex-flow: unset;
      overflow-x: scroll;
      .tablet-col {
        flex: 0 0 225px;
        max-width: 225px;
      }
    }
  }
  .main-message{
    color: ${primaryHeadingColor};
    font-weight:${wMBold};
    font-size:28px;
    font-family:${secondaryFont};
    margin:0;
    text-align:center;
    @media all and (max-width: ${maxMobile}) {
      font-size: 18px;
    }
  }
  .groups-row {
    @media all and (max-width: ${minWidthApp}) {
      .ant-col {
        padding-left: 4px !important;
        padding-right: 4px !important;
      }
    }
  }
`;

const Heading = styled.h3`
  display: flex;
  align-items: center;
  font-size: 28px;
  font-weight: ${wMedium};
  font-family: ${secondaryFont};
  line-height: 30px;
  margin-bottom: 0px;
  color: ${primaryHeadingColor};
  @media all and (max-width: ${maxWidth}) , ${retina} {
    font-size: 24px;
  }
  @media all and (max-width: ${maxTablet}) {
    font-size: 19px;
  }
`;

const MostActiveGroups = function MostActiveGroups(props: Props) {
  const {
    title, groupCount, groups, loading, dragging, loaded,
  } = props;
  const formattedGroupCount = formatNumberWithCommas(groupCount);
  const isEnterprise = isEnterpriseUser();

  const host = window.location.href;
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const isMob = IsMobile();
  const toggle = (flag: boolean) => {
    setOpenCreateModal(flag);
  };

  const data = useSelector(selectData);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(certificationActions.certificationAction({
      type: 'people_dashboard',
      source: 'buyer',
      data,
    }));
  }, []);

  useEffect(() => {
    setShowNew(false);
    setOpenCreateModal(dragging);
  }, [dragging]);

  const openModal = () => {
    setOpenCreateModal(true);
    setShowNew(true);
    dispatch(certificationActions.certificationAction({
      type: 'create_new',
      source: 'buyer',
      data,
    }));
    dispatch(flurryActions.callFlurryEvent(
      valueToObject('peopleDashboard_createNew_buyerGroup_clicked', 0, {
        host,
      }),
    ));
  };

  return (
    <Wrapper>
      <div className="head-wrapper smMob-wrapper">
        {loading && !loaded ? <SkeletonCustom width={isMob ? '200px' : '340px'} height="30px" margin="0px" />
          : (
            <Heading>
              {title}
              {' '}
              {' '}
              (
              <span className={` ${translateSkipAreas}`}>{` ${formattedGroupCount}`}</span>
              )
              <HeadingWithToolTip
                heightIconXSm="17px"
                heightIcon="18px"
                toolTipText={TOOLTIP_BUYERGROUP}
                videoLink={VIDEO_BUYERGROUP}
                pageType="people"
              />
            </Heading>
          )}
        <div className="right-area most-active-group">
          <DrawerPeopleGroup
            openNewCreate={openCreateModal}
            setOpenNew={toggle}
            showNew={showNew}
            toggleShowNew={(bool: boolean) => setShowNew(bool)}
            noButton={false}
            drag={dragging}
          />
          {isEnterprise && <DrawerPersonas />}
          <GlobalButton
            title="Create New"
            textColor="#ffffff"
            fontSize="14px"
            bgColor="#001760"
            borderRadius="6px"
            innerSpace="8px 10px"
            PaddingSm="4px 13px"
            onButtonClick={openModal}
            buttonClassName="create-new-button"
          />
        </div>
      </div>
      <Row gutter={[12, 12]} className="tablet-row">
        {loading && (!loaded || !groups.length) ? [1, 2, 3, 4].map(() => (
          <Col lg={{ span: 6 }} xs={{ span: 24 }} sm={{ span: 12 }} className="tablet-col">
            <BuyerGroupSkeleton border />
          </Col>
        ))
          : groups.length > 0 && groups.slice(0, 4).map((group) => (
            <Col lg={{ span: 6 }} xs={{ span: 24 }} sm={{ span: 12 }} className="tablet-col">
              <BuyerGroupCard
                key={group.groupId}
                groupId={group.groupId}
                groupName={group.groupName}
                groupPersona={group.groupPersonality}
                persons={group.persons}
                companies={group.companyLogos}
                members={group.personsCount}
                isShared={group.groupShared}
                sharedBy={group.sharedByFirstName}
                pin={group.pin}
              />
            </Col>
          ))}
        {groups.length < 1 && !loading && loaded ? <NoPersonsFound title={NO_GROUP_FOUND} /> : null}
      </Row>
    </Wrapper>
  );
};

export default MostActiveGroups;
